import React, { memo, useCallback, useEffect, useRef, useState } from "react";

// utils
import { captureError, useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { fetchLottie, modifyLottie } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";

// constants
import {
    GRATIFICATION_POPUP_LOTTIE_TIMEOUT,
    LOTTIE_PLAYER_CONTAINER_TEST_ID,
} from "@super-ui/components/m2c-swiggy-one-halfcard/constants";

// types
import { LOTTIE_ERROR_TYPES } from "@swiggy-private/core-types";
import { AnimationItem, LottiePlayer } from "lottie-web/build/player/lottie_light";
import { ILottiePlayer } from "./types";

//styles
import { LottiePlayerContainer } from "../../styles";

const LottiePlayerComp = ({ onError, domLoadedCallbacks, ...props }: ILottiePlayer) => {
    const animationContainer = useRef<HTMLDivElement>(null);
    const animationInstance = useRef<AnimationItem>();
    const timerId = useRef<NodeJS.Timeout | null>(null);

    const [playing, setPlaying] = useState(false);

    const [domLoaded, setDomLoaded] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleError = useCallback(
        (errorType?: LOTTIE_ERROR_TYPES, error?: unknown) => {
            if (typeof onError === "function") {
                onError(errorType, error);
            }
        },
        [onError],
    );

    const fetchLottieData = useCallback(async () => {
        try {
            const Lottie = (await import("lottie-web/build/player/lottie_light")).default as LottiePlayer;
            const lottieData = await fetchLottie({
                ...props,
                onError: handleError,
            });

            const updatedLottie = await modifyLottie({
                onError,
                lottieData,
                ...props,
            });

            if (animationContainer.current) {
                animationInstance.current = Lottie.loadAnimation({
                    container: animationContainer.current,
                    renderer: "svg",
                    loop: false,
                    autoplay: true,
                    animationData: updatedLottie,
                    rendererSettings: {
                        hideOnTransparent: true,
                        progressiveLoad: true,
                    },
                });

                animationInstance.current.addEventListener("DOMLoaded", () => {
                    setDomLoaded(true);
                    domLoadedCallbacks?.forEach(callback => callback());
                });

                animationInstance.current.addEventListener("loaded_images", () => {
                    setImageLoaded(true);
                });

                animationInstance.current.addEventListener("complete", () => {
                    if (!playing) {
                        setPlaying(true);
                    }
                });

                animationInstance?.current?.addEventListener("error", e => {
                    handleError(LOTTIE_ERROR_TYPES.LOTTIE_ERROR, e);
                });
            }
        } catch (err) {
            captureError(err);
            handleError(LOTTIE_ERROR_TYPES.FETCH_FAILED, err);
        }
    }, [domLoadedCallbacks, handleError, onError, playing, props]);

    useEffect(() => {
        // if dom and images are loaded before the specific timeout
        // then we clearTimeout and play the animation
        if (domLoaded && imageLoaded && !playing) {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        } else if (!timerId.current) {
            // if dom and images are not loaded within the specific timeout
            // then we will fallback to the non-lottie experience
            timerId.current = setTimeout(() => {
                if (!(domLoaded && imageLoaded)) {
                    handleError(LOTTIE_ERROR_TYPES.TIMEOUT);
                }
            }, GRATIFICATION_POPUP_LOTTIE_TIMEOUT);
        }

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
    }, [domLoaded, handleError, imageLoaded, playing]);

    useEffectOnce(() => {
        let hasAnyEmptyValue = false;
        if ("isGratificationPopup" in props) {
            hasAnyEmptyValue = Object.values(props.gratificationPopUpData ?? {}).some(value => isEmpty(value));
        } else if ("isPlanRemovalHalfCard" in props) {
            hasAnyEmptyValue = Object.values(props.planRemovalHalfCardData ?? {}).some(value => isEmpty(value));
        }
        if (hasAnyEmptyValue) {
            handleError(LOTTIE_ERROR_TYPES.INCOMPLETE_DATA);
        } else {
            fetchLottieData();
        }

        return () => {
            if (animationInstance.current) {
                animationInstance.current.destroy();
            }
        };
    });

    return <LottiePlayerContainer data-testid={LOTTIE_PLAYER_CONTAINER_TEST_ID} ref={animationContainer} />;
};

export default memo(LottiePlayerComp);
